import React from 'react';
import PropTypes from 'prop-types';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';

const bgShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  image_url: PropTypes.string,
});

// Two-panel background picker for /admin/background_sets/:id/edit.
// Left: filterable grid of every non-archived background not yet selected.
// Right: this set's backgrounds, drag-to-reorder, click ✕ to remove.
// Submits the same `background_set[background_ids]=1,2,3` hidden input as
// the prior text-chip picker, so the controller is unchanged.

// Each tile is a 100x100 slot; the image fits inside its natural aspect
// (object-fit: contain), so a portrait background renders ~80x100 and a
// landscape one ~100x80 -- the customer-facing orientation is preserved.
const SLOT = 100;

const slotStyle = {
  width: SLOT,
  height: SLOT,
  border: '1px solid #ccc',
  borderRadius: 3,
  background: '#f0f0f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const imgStyle = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
};

// react-sortable-hoc handle -- the whole thumb is the drag handle here so
// the entire card is grabbable. onMouseDown must be suppressed on the ✕ so
// clicking the remove button doesn't kick off a drag.
const Handle = SortableHandle(({ src, alt }) => (
  <div style={slotStyle}>
    <img src={src} alt={alt} draggable={false} style={imgStyle} />
  </div>
));

const SelectedTile = SortableElement(({ bg, onRemove }) => (
  <div
    style={{
      position: 'relative',
      width: SLOT,
      height: SLOT,
      margin: 4,
      cursor: 'grab',
    }}
    title={bg.color}
  >
    <Handle src={bg.image_url} alt={bg.color} />
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => onRemove(bg.id)}
      aria-label={`Remove ${bg.color}`}
      style={{
        position: 'absolute',
        top: -6,
        right: -6,
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '1px solid #999',
        background: '#fff',
        color: '#333',
        fontSize: 11,
        lineHeight: '16px',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      ×
    </button>
  </div>
));

const SelectedGrid = SortableContainer(({ items, onRemove }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: SLOT + 20,
      padding: 8,
      border: '1px solid #ddd',
      borderRadius: 4,
      background: '#fafafa',
    }}
  >
    {items.length === 0 && (
      <div style={{ color: '#999', fontSize: 12, padding: '4px 8px' }}>
        No backgrounds selected -- click a thumbnail on the left to add one.
      </div>
    )}
    {items.map((bg, index) => (
      <SelectedTile key={bg.id} index={index} bg={bg} onRemove={onRemove} />
    ))}
  </div>
));

Handle.propTypes = { src: PropTypes.string, alt: PropTypes.string };
SelectedTile.propTypes = { bg: bgShape.isRequired, onRemove: PropTypes.func.isRequired };
SelectedGrid.propTypes = {
  items: PropTypes.arrayOf(bgShape).isRequired,
  onRemove: PropTypes.func.isRequired,
};

const AvailableTile = ({ bg, onAdd }) => (
  <button
    type="button"
    onClick={() => onAdd(bg.id)}
    title={`Add ${bg.color}`}
    style={{
      width: SLOT,
      height: SLOT,
      margin: 4,
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
    }}
  >
    <div style={slotStyle}>
      <img src={bg.image_url} alt={bg.color} style={imgStyle} />
    </div>
  </button>
);

AvailableTile.propTypes = { bg: bgShape.isRequired, onAdd: PropTypes.func.isRequired };

export default function BackgroundSetPicker({ inputName, available = [], selected = [] }) {
  const [selectedIds, setSelectedIds] = React.useState(selected.map((b) => b.id));
  const [query, setQuery] = React.useState('');

  // Index every known background by id so we can move records between
  // available and selected without re-fetching. Both props are frozen at
  // mount; we track *ids* to derive the current split.
  const byId = React.useMemo(() => {
    const m = new Map();
    [...available, ...selected].forEach((b) => m.set(b.id, b));
    return m;
  }, [available, selected]);

  const selectedList = selectedIds.map((id) => byId.get(id)).filter(Boolean);

  const availableList = React.useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const q = query.trim().toLowerCase();
    return [...byId.values()]
      .filter((bg) => !selectedSet.has(bg.id))
      .filter((bg) => !q || bg.color.toLowerCase().includes(q))
      .sort((a, b) => a.color.localeCompare(b.color));
  }, [byId, selectedIds, query]);

  const add = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const remove = (id) => setSelectedIds((prev) => prev.filter((x) => x !== id));

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    setSelectedIds((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      return next;
    });
  };

  return (
    <>
      <input type="hidden" name={inputName} value={selectedIds.join(',')} />

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Left: narrower + visually downplayed picker (source of adds). */}
        <div style={{ flex: '0 0 260px', minWidth: 0, opacity: 0.85 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 400,
            color: '#888',
            marginBottom: 4,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
          >
            Available (click to add)
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter..."
            style={{
              width: '100%',
              padding: '3px 6px',
              border: '1px solid #ddd',
              borderRadius: 3,
              fontSize: 11,
              marginBottom: 6,
              boxSizing: 'border-box',
              background: '#fafafa',
            }}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              maxHeight: 480,
              overflowY: 'auto',
              padding: 6,
              border: '1px solid #e5e5e5',
              borderRadius: 4,
              background: '#f7f7f7',
            }}
          >
            {availableList.length === 0 && (
              <div style={{ color: '#aaa', fontSize: 11, padding: '4px 8px' }}>
                {query ? 'No matches.' : 'Every background is in this set.'}
              </div>
            )}
            {availableList.map((bg) => (
              <AvailableTile key={bg.id} bg={bg} onAdd={add} />
            ))}
          </div>
        </div>

        {/* Right: the actual selection customers see -- prominent. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, marginBottom: 4, color: '#222',
          }}
          >
            In this set (
            {selectedList.length}
            ) — drag to reorder
          </div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
            This is what customers will see, in this order. First one is the default.
          </div>
          <SelectedGrid
            items={selectedList}
            onRemove={remove}
            onSortEnd={onSortEnd}
            axis="xy"
            distance={4}
            useDragHandle
            getHelperDimensions={({ node }) => node.getBoundingClientRect()}
          />
        </div>
      </div>
    </>
  );
}

BackgroundSetPicker.propTypes = {
  inputName: PropTypes.string.isRequired,
  available: PropTypes.arrayOf(bgShape),
  selected: PropTypes.arrayOf(bgShape),
};
BackgroundSetPicker.defaultProps = { available: [], selected: [] };
